import { SearchModel } from '@/components/RunsTable/types';
import { EProviderSecretName } from '@/hooks/useHasWorkspaceSecrets';

import {
  Attribute,
  InsightsJobConfigData,
  InsightsJobFormData,
  PartitionPair,
  TimeRange,
} from './types';

export const DEFAULT_SUMMARY_PROMPT = `The following is a trace for an interaction between an AI assistant and a user:

<trace>
<inputs>
{{run.inputs}} 
</inputs>

<outputs>
{{run.outputs}}
</outputs>
</trace>

Your job is to answer the question: what was the user's overall request for the assistant?

To answer this well: identify what domain/product area this falls into, then focus on both the technical details that matter for that domain AND the type of task (e.g., building, debugging, planning, brainstorming, learning, troubleshooting, etc.) when summarizing the user's request.

Output a 10-18 word summary in English. Be specific, clear, and concise. Write as a complete, readable sentence that flows naturally - not as bullet points or technical specifications. Don't say "Based on the conversation..." or "We discussed..."

These summaries will be analyzed to identify patterns in user requests, common problems, and feature usage across conversations.`;

export const DEFAULT_TIME_RANGE: TimeRange = { last_n_hours: 24 };
export const DEFAULT_SAMPLE_SIZE = 100;
export const DEFAULT_FILTER: SearchModel = {
  filter: 'eq(is_root, true)',
};

export const DEFAULT_PARTITION_PAIRS: PartitionPair[] = [
  { key: '', value: '', id: `partition-${Date.now()}-${Math.random()}` },
];

export const DEFAULT_ATTRIBUTES: Attribute[] = [
  {
    name: '',
    description: '',
    type: 'boolean',
  },
];

export enum ConfigSection {
  SELECT_RUNS = 'Select Runs',
  CATEGORIES = 'Categories',
  SUMMARY_PROMPT = 'Summary Prompt',
  ATTRIBUTES = 'Attributes',
}

export const CONFIG_SECTIONS = Object.values(ConfigSection);

export const CONFIG_SETTINGS_SECTIONS: Record<
  keyof InsightsJobFormData,
  ConfigSection
> = {
  name: ConfigSection.SELECT_RUNS,
  filter: ConfigSection.SELECT_RUNS,
  timeRange: ConfigSection.SELECT_RUNS,
  sample: ConfigSection.SELECT_RUNS,
  partitionPairs: ConfigSection.CATEGORIES,
  summaryPrompt: ConfigSection.SUMMARY_PROMPT,
  attributes: ConfigSection.ATTRIBUTES,
};

// Used for showing diff between old and new config
export const CONFIG_SETTINGS_LABELS: Record<
  keyof InsightsJobConfigData,
  string
> = {
  name: 'Job Name',
  start_time: 'Time Range',
  end_time: 'Time Range',
  last_n_hours: 'Time Range',
  hierarchy: 'Hierarchy',
  sample: 'Sample Size',
  filter: 'Filter',
  partitions: 'Categories',
  summary_prompt: 'Summary Prompt',
  attribute_schemas: 'Attributes',
};

export const CUSTOM_TIME_RANGE_VALUE = -1;
export const TIME_RANGE_OPTIONS = [
  { value: 1, label: '1h' },
  { value: 6, label: '6h' },
  { value: 24, label: '24h' },
  { value: 168, label: '1 week' },
  { value: 720, label: '1 month' },
  { value: CUSTOM_TIME_RANGE_VALUE, label: 'Custom' },
];

export const SAMPLE_SIZE_MIN = 1;
export const SAMPLE_SIZE_MAX = 1000;

export const REQUIRED_API_KEYS = [EProviderSecretName.OPENAI];

export const DISMISSED_SUMMARY_PROMPT_INSTRUCTIONS_LS_KEY =
  'ls:insights-config-form:dismissed-summary-prompt-instructions';

export const DEFAULT_SEARCH_MODEL = {
  filter: 'eq(is_root, true)',
  traceFilter: undefined,
  treeFilter: undefined,
};

// We expect the default prebuilt config to be the first config in the list returned from the API
export const DEFAULT_CONFIG_ID = '0';
